/**
 * Questo file definisce il componente React per la sessione di studio privata.
 * - Gestisce la logica di visualizzazione delle flashcard una per una.
 * - Permette all'utente di girare la card, segnarla come corretta o sbagliata.
 * - Calcola e mostra i risultati alla fine della sessione.
 * - Offre opzioni per ricominciare lo studio o studiare solo le card sbagliate.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import './StudySession.css';

/**
 * FUNZIONE DI UTILITÀ per mescolare un array.
 * - Implementa l'algoritmo di Fisher-Yates (noto anche come Knuth shuffle).
 * - Prende un array come input e lo restituisce mescolato in modo casuale.
 * - È essenziale per presentare le flashcard in un ordine sempre diverso.
 */
const shuffleArray = (array) => {
    let currentIndex = array.length, randomIndex;
    // Continua a mescolare finché ci sono elementi da considerare.
    while (currentIndex !== 0) {
        // Sceglie un elemento rimanente a caso.
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        // E lo scambia con l'elemento corrente.
        [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
};

/**
 * COMPONENTE PRINCIPALE: StudySession
 * - Gestisce l'intera interfaccia e la logica di una sessione di studio.
 */
export default function StudySession() {
    // Estrae l'ID della cartella dai parametri dell'URL.
    const { folderId } = useParams();
    // Hook per la navigazione programmatica.
    const navigate = useNavigate();

    /**
     * ESTRAZIONE DATI DALLO STORE REDUX
     * - `useSelector` si collega allo stato di Redux.
     * - Cerca e seleziona la cartella specifica per questa sessione di studio
     * basandosi sul `folderId` ottenuto dall'URL.
     */
    const folder = useSelector(state => 
        state.flashcards.detailedFolders.find(f => f.id.toString() === folderId)
    );

    /**
     * STATI DEL COMPONENTE
     * - `studyDeck`: L'array di flashcard mescolate per la sessione corrente.
     * - `currentCardIndex`: L'indice della card attualmente visualizzata.
     * - `isFlipped`: Un booleano che indica se la card corrente è girata.
     * - `results`: Un array che memorizza i risultati (corretto/sbagliato) per ogni card.
     * - `sessionState`: Lo stato della sessione ('loading', 'studying', 'finished').
     */
    const [studyDeck, setStudyDeck] = useState([]);
    const [currentCardIndex, setCurrentCardIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [results, setResults] = useState([]);
    const [sessionState, setSessionState] = useState('loading');

    /**
     * EFFETTO: Inizializzazione della sessione di studio.
     * - `useEffect` viene eseguito quando il componente viene montato o quando `folder` o `Maps` cambiano.
     * - Se la cartella esiste e ha delle flashcard, le mescola e imposta lo stato della sessione su 'studying'.
     * - Se la cartella non viene trovata (es. dopo un ricaricamento della pagina), reindirizza l'utente
     * alla pagina delle sue cartelle per evitare errori.
     */
    useEffect(() => {
        if (folder && folder.flashcards.length > 0) {
            setStudyDeck(shuffleArray([...folder.flashcards]));
            setSessionState('studying');
        } else if (!folder) {
            navigate('/my-flashcards');
        }
    }, [folder, navigate]);

    /**
     * MEMOIZZAZIONE: Ottimizza il calcolo della card corrente.
     * - `useMemo` ricalcola `currentCard` solo se `studyDeck` o `currentCardIndex` cambiano.
     * - Questo previene ricalcoli inutili ad ogni renderizzazione del componente.
     */
    const currentCard = useMemo(() => {
        return studyDeck[currentCardIndex];
    }, [studyDeck, currentCardIndex]);

    /**
     * GESTORE EVENTO: Girare la card.
     * - Inverte semplicemente lo stato booleano `isFlipped`.
     */
    const handleFlip = () => {
        setIsFlipped(!isFlipped);
    };

    /**
     * GESTORE EVENTO: Rispondere a una domanda.
     * - Registra il risultato (corretto o sbagliato) per la card corrente.
     * - Se ci sono altre card nel mazzo, passa alla successiva e resetta lo stato `isFlipped`.
     * - Altrimenti, imposta lo stato della sessione su 'finished'.
     */
    const handleAnswer = (isCorrect) => {
        setResults([...results, { cardId: currentCard.id, correct: isCorrect }]);
        
        if (currentCardIndex < studyDeck.length - 1) {
            setCurrentCardIndex(currentCardIndex + 1);
            setIsFlipped(false);
        } else {
            setSessionState('finished');
        }
    };
    
    /**
     * GESTORE EVENTO: Ricominciare la sessione.
     * - Può ricominciare con tutte le card o solo con quelle sbagliate.
     * - Se `onlyMistakes` è true, filtra le card in base ai risultati salvati.
     * - Mescola il nuovo mazzo e resetta tutti gli stati della sessione.
     */
    const handleRestart = (onlyMistakes = false) => {
        let newDeck;
        if (onlyMistakes) {
            const mistakeIds = results.filter(r => !r.correct).map(r => r.cardId);
            newDeck = folder.flashcards.filter(card => mistakeIds.includes(card.id));
        } else {
            newDeck = [...folder.flashcards];
        }

        setStudyDeck(shuffleArray(newDeck));
        setCurrentCardIndex(0);
        setIsFlipped(false);
        setResults([]);
        setSessionState('studying');
    };

    // --- VISTE CONDIZIONALI RENDERIZZATE ---

    // Vista di caricamento: mostrata mentre si attende il caricamento dei dati della cartella.
    if (sessionState === 'loading' || !folder) {
        return <div className="study-session-loading">Caricamento sessione...</div>;
    }

    // Vista per cartella vuota: mostrata se la cartella non contiene flashcard.
    if (folder.flashcards.length === 0) {
        return (
            <div className="study-session-container">
                <div className="results-screen">
                    <h2>Cartella Vuota</h2>
                    <p>Questa cartella non contiene flashcard da studiare.</p>
                    <Link to="/my-flashcards" className="btn btn-primary">Torna alle mie cartelle</Link>
                </div>
            </div>
        );
    }
    
    // Vista finale: mostrata al termine della sessione.
    if (sessionState === 'finished') {
        const correctAnswers = results.filter(r => r.correct).length;
        const totalAnswers = results.length;
        const score = totalAnswers > 0 ? Math.round((correctAnswers / totalAnswers) * 100) : 0;

        return (
            <div className="study-session-container">
                <div className="results-screen">
                    <h2>Sessione Completata!</h2>
                    <p className="score">Punteggio: {score}%</p>
                    <p>Hai risposto correttamente a {correctAnswers} su {totalAnswers} domande.</p>
                    <div className="results-actions">
                        <button onClick={() => handleRestart(false)} className="btn btn-primary">Ricomincia</button>
                        {/* Mostra il pulsante per studiare gli errori solo se ce ne sono. */}
                        {correctAnswers < totalAnswers && (
                            <button onClick={() => handleRestart(true)} className="btn btn-outline-secondary">Studia solo quelle sbagliate</button>
                        )}
                        <Link to="/my-flashcards" className="btn btn-outline-secondary">Torna alle mie cartelle</Link>
                    </div>
                </div>
            </div>
        );
    }

    // Vista principale: la sessione di studio attiva.
    return (
        <div className="study-session-container">
            {/* Header della sessione con pulsante di uscita e barra di progresso */}
            <div className="study-header">
                <Link to="/my-flashcards" className="exit-button" title="Esci dalla sessione">
                    <i className="bi bi-x-lg"></i>
                </Link>
                <div className="progress-bar-container">
                    <div 
                        className="progress-bar-fill" 
                        style={{ width: `${((currentCardIndex + 1) / studyDeck.length) * 100}%` }}
                    ></div>
                </div>
                <div className="progress-text">{currentCardIndex + 1} / {studyDeck.length}</div>
            </div>

            {/* Area centrale dove viene mostrata la flashcard */}
            <div className="study-card-area">
                <div className={classNames('study-card-scene', { 'is-flipped': isFlipped })}>
                    {/* Fronte della card (domanda) */}
                    <div className="study-card-face study-card-front">
                        <div className="card-content-study">
                            <small>Domanda</small>
                            <p>{currentCard.domanda}</p>
                        </div>
                    </div>
                    {/* Retro della card (risposta) */}
                    <div className="study-card-face study-card-back">
                        <div className="card-content-study">
                            <small>Risposta</small>
                            <p>{currentCard.risposta}</p>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Controlli della sessione (gira la card o rispondi) */}
            <div className="study-controls">
                {!isFlipped ? (
                    // Se la card non è girata, mostra il pulsante per girarla.
                    <button onClick={handleFlip} className="btn btn-primary btn-lg flip-button">Gira la card</button>
                ) : (
                    // Se la card è girata, mostra i pulsanti per segnare la risposta.
                    <div className="answer-buttons">
                        <button onClick={() => handleAnswer(false)} className="btn btn-danger btn-lg">Sbagliato</button>
                        <button onClick={() => handleAnswer(true)} className="btn btn-success btn-lg">Corretto</button>
                    </div>
                )}
            </div>
        </div>
    );
}